import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Category } from '../../../handling_data/models/Category';
import { Owner } from '../../../handling_data/services/OwnerService';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';

interface BasicInfoStepProps {
    t: any;
    formData: CreatePropertyDto;
    categories: Category[];
    owners: Owner[];
}

export interface StepRef {
    getData: () => Partial<CreatePropertyDto>;
}

const BasicInfoStep = forwardRef<StepRef, BasicInfoStepProps>(({ t, formData, categories, owners }, ref) => {
    const { i18n } = useTranslation();
    const [localData, setLocalData] = useState({
        ar: { ...formData.ar },
        en: { ...formData.en },
        tr: { ...formData.tr },
        category_id: formData.category_id,
        user_id: formData.user_id,
        contact_info: formData.contact_info
    });

    // Sync local state when formData changes (essential for edit flow)
    useEffect(() => {
        setLocalData({
            ar: { ...formData.ar },
            en: { ...formData.en },
            tr: { ...formData.tr },
            category_id: formData.category_id,
            user_id: formData.user_id,
            contact_info: formData.contact_info
        });
    }, [formData.ar, formData.en, formData.tr, formData.category_id, formData.user_id, formData.contact_info]);

    useImperativeHandle(ref, () => ({
        getData: () => localData
    }));

    const updateLangField = (lang: 'ar' | 'en' | 'tr', field: 'title' | 'description', value: string) => {
        setLocalData(prev => ({
            ...prev,
            [lang]: { ...prev[lang], [field]: value }
        }));
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('property_form.basic_info')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['ar', 'en', 'tr'].map(lang => (
                    <div key={lang}>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t(`property_form.title_${lang}`)}</label>
                        <input
                            type="text"
                            required
                            className={`w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 ${lang === 'en' ? 'dir-ltr' : ''}`}
                            value={localData[lang as 'ar' | 'en' | 'tr'].title}
                            onChange={(e) => updateLangField(lang as 'ar' | 'en' | 'tr', 'title', e.target.value)}
                        />
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('property_form.category')}</label>
                    <select
                        required
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20"
                        value={localData.category_id?.toString() || ''}
                        onChange={(e) => setLocalData({ ...localData, category_id: Number(e.target.value) })}
                    >
                        <option value="">{t('common.select')}</option>
                        {categories.map((category) => (
                            <option key={category.id} value={category.id.toString()}>
                                {category[i18n.language as 'ar' | 'en' | 'tr']?.name || category.en?.name || 'Unknown'}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('property_form.owner')}</label>
                    <select
                        required
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20"
                        value={localData.user_id?.toString() || ''}
                        onChange={(e) => {
                            const userId = Number(e.target.value);
                            const selectedOwner = owners.find(o => o.id === userId);
                            setLocalData({
                                ...localData,
                                user_id: userId,
                                contact_info: selectedOwner?.profile?.contact_phone || selectedOwner?.phone || localData.contact_info
                            });
                        }}
                    >
                        <option value="">{t('common.select')}</option>
                        {owners.map(owner => (
                            <option key={owner.id} value={owner.id.toString()}>
                                {owner.profile?.official_name || owner.name || `Owner #${owner.id}`}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {['ar', 'en', 'tr'].map(lang => (
                    <div key={lang}>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t(`property_form.desc_${lang}`)}</label>
                        <textarea
                            rows={4}
                            className={`w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 ${lang === 'en' ? 'dir-ltr' : ''}`}
                            value={localData[lang as 'ar' | 'en' | 'tr'].description}
                            onChange={(e) => updateLangField(lang as 'ar' | 'en' | 'tr', 'description', e.target.value)}
                        />
                    </div>
                ))}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">بيانات التواصل (رقم الهاتف)</label>
                <input
                    type="text"
                    readOnly
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed dir-ltr"
                    value={localData.contact_info}
                />
            </div>
        </div>
    );
});

export default BasicInfoStep;
